import { useRuntimeConfig } from '@borradh-workspace/runtime-config/client';
import { Capacitor } from '@capacitor/core';
import posthog from 'posthog-js';
import {
  PostHogProvider as PHProvider,
  useFeatureFlagEnabled,
  useFeatureFlagPayload,
} from 'posthog-js/react';
import { useEffect, useRef } from 'react';

import {
  installBrowserConsoleAndNetworkMirroring,
  mirrorPostHogEvent,
} from '@/lib/betterstack-browser-events';
import { sanitizeBrowserTelemetry } from '@/lib/browser-telemetry';
import { reportMobileBootVersions } from '@/lib/capgo';
import { flushPostHogErrorQueue } from '@/lib/log-error';
import { onePageviewPerUrl } from '@/lib/one-pageview-per-url';
import {
  applyPostHogActivityIdentity,
  clearPostHogActivityIdentity,
  getBrowserActivityProps,
  setPostHogActivityIdentity,
  stampActivityAttribution,
} from '@/lib/posthog-activity';
import {
  IMPERSONATION_POSTHOG_PERSISTENCE,
  discardImpersonationPostHogState,
  isImpersonatingTab,
} from '@/lib/posthog-impersonation';
import { dropPostHogNoise } from '@/lib/posthog-noise-filter';
import { syncSessionRecording } from '@/lib/session-recording-choice';
import { getPosthogKey, trackEvent } from '@/lib/track-event';
import {
  consumeBuildRefreshReceipt,
  currentBuildId,
} from '@/lib/web-build-refresh';
import { router } from '@/router';
import type { ActivityIdentity } from '@borradh-workspace/observability/activity-attribution';
import { sanitizeTelemetryUrl } from '@borradh-workspace/observability/telemetry-sanitizer';

/**
 * PostHog for the Vite app: feature flags + optional capture.
 * Init only when `posthogKey` is set in runtime config (matches Next web behavior when key absent).
 */
export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const { posthogKey, posthogHost, appEnv } = useRuntimeConfig();
  const initialized = useRef(false);

  useEffect(() => {
    installBrowserConsoleAndNetworkMirroring();
  }, []);

  // Web bundle ALWAYS routes PostHog through the same-origin /r3y proxy (Vercel
  // rewrites it to eu.i.posthog.com, incl. /static for the recorder). The path
  // is a deliberately opaque token — NOT the well-known `/ingest`, which blocker
  // lists (uBlock/AdGuard/EasyPrivacy) now match by path. `posthogHost` must NOT
  // override this on web: when set, posthog-js would hit eu.i.posthog.com
  // directly and blockers kill every request (and the recorder module import).
  // Native (Capacitor) has no proxy server, so it uses posthogHost / direct.
  const apiHost =
    Capacitor.getPlatform() === 'web'
      ? '/r3y'
      : (posthogHost ?? 'https://eu.i.posthog.com');

  useEffect(() => {
    if (initialized.current) return;
    if (!posthogKey) return;

    // An impersonating tab gets its own identity and session (so its own
    // replay), kept off the store the admin's tabs share — see
    // posthog-impersonation.ts. Any other load ends the last one.
    const impersonating = isImpersonatingTab();
    if (!impersonating) discardImpersonationPostHogState();

    posthog.init(posthogKey, {
      api_host: apiHost,
      ui_host: 'https://eu.posthog.com',
      capture_pageview: false,
      capture_pageleave: true,
      persistence: 'localStorage+cookie',
      ...(impersonating ? IMPERSONATION_POSTHOG_PERSISTENCE : {}),
      respect_dnt: true,
      // Off until the route is known, so a patient's page never starts a
      // recording. The route effect below turns it on for staff routes.
      disable_session_recording: true,
      // Exception autocapture is OFF on purpose. It installs its handlers from
      // a lazily-fetched `exception-autocapture.js`, so it is armed late (and,
      // if that request is blocked, never) — which left boot-time unhandled
      // errors in Sentry with no PostHog twin (WEB-15, WEB-29). We install the
      // listeners ourselves, synchronously, in lib/global-error-handlers.ts.
      // Turning this back on would double-capture every unhandled error.
      capture_exceptions: false,
      // Drop the same non-defects Sentry drops (extension injection, expected
      // user conditions, one client's connectivity). Without this, PostHog
      // inherits noise Sentry has been absorbing — and that noise is what made
      // the Sentry issue stream unreadable in the first place.
      before_send: [
        dropPostHogNoise,
        stampActivityAttribution,
        mirrorPostHogEvent,
        (event) => sanitizeBrowserTelemetry(event, posthogKey),
      ],
    });

    initialized.current = true;

    // Super property on every event so staging/preview data stays separable
    // from production in the shared PostHog project. `impersonation` marks
    // every event (and so every replay) an admin produced as a customer.
    // `app_build` rides along too, exceptions included: "is this error only
    // coming from a build that is no longer live?" becomes a filter instead of
    // a hunt through stack-frame bundle hashes.
    posthog.register({
      environment: appEnv,
      app_build: currentBuildId,
      ...getBrowserActivityProps(),
    });
    applyPostHogActivityIdentity();

    // This tab was moved onto this build by lib/web-build-refresh.ts. The
    // outgoing page could not report it — it was unloading.
    const refreshed = consumeBuildRefreshReceipt();
    if (refreshed) {
      posthog.capture('web_build_refreshed', {
        from_build: refreshed.from,
        to_build: refreshed.to,
        trigger: refreshed.trigger,
        // False means the reload did not land on the build it was aiming for.
        landed: refreshed.to === currentBuildId,
      });
    }

    // Replay anything that failed before posthog-js was ready — the window
    // between Sentry's synchronous init and this effect.
    flushPostHogErrorQueue();

    // One `mobile_boot_version` event per launch (native version, OTA bundle
    // version/id, channel) — see capgo.ts for why this has to fire here
    // rather than from main.tsx's earlier boot sequence.
    void reportMobileBootVersions();

    // Capture the initial pageview, then one per client-side navigation. This
    // provider sits ABOVE RouterProvider, so we subscribe to the router
    // imperatively rather than via useRouterState (no router context here).
    //
    // `onResolved` is NOT "a navigation finished". The router emits it whenever
    // its pending/transition state settles, and every `updateMatch` runs in a
    // transition — including the ones a hover preload (`defaultPreload:
    // 'intent'`) makes. So moving the mouse over the sidebar logged the CURRENT
    // page as a fresh view, several times a second: 169 such bursts in 83
    // production sessions in one week, and one of them read as "the page
    // reloaded 100 times" during a support investigation. Only a changed URL
    // is a pageview.
    const captureNewUrl = onePageviewPerUrl((href) => {
      posthog.capture('$pageview', {
        $current_url: sanitizeTelemetryUrl(href),
      });
    });
    const capturePageview = () => {
      if (!posthog.__loaded) return;
      captureNewUrl(window.location.href);
    };
    capturePageview();
    return router.subscribe('onResolved', capturePageview);
  }, [posthogKey, apiHost, appEnv]);

  // Its own effect: the one above returns early on a re-run, so under StrictMode
  // its subscription doesn't survive.
  useEffect(() => {
    if (!posthogKey) return;
    const syncForRoute = () =>
      syncSessionRecording(router.state.matches.map((match) => match.routeId));
    syncForRoute();
    return router.subscribe('onResolved', syncForRoute);
  }, [posthogKey]);

  if (!posthogKey) {
    return <>{children}</>;
  }

  return <PHProvider client={posthog}>{children}</PHProvider>;
}

export function identifyUser(
  userId: string,
  properties?: {
    email?: string;
    name?: string;
    [key: string]: unknown;
  },
  activity?: Omit<ActivityIdentity, 'userId'>
) {
  if (!getPosthogKey()) return;
  setPostHogActivityIdentity({ ...activity, userId }, properties);
}

export function setUserGroup(
  groupType: string,
  groupId: string,
  properties?: Record<string, unknown>
) {
  if (!getPosthogKey()) return;
  posthog.group(groupType, groupId, properties);
}

export function resetUser() {
  clearPostHogActivityIdentity();
  if (!getPosthogKey()) return;
  posthog.reset();
}

// Router-free, so a feature hook can capture without dragging the route tree
// in through this module. See @/lib/track-event.
export { trackEvent };

/**
 * Reactively read a boolean feature flag for the current user/org. Re-renders
 * when the flag value changes. Defaults to `false` while flags load or when
 * PostHog is disabled — the safe default for canary gating (off until proven on).
 *
 * Flags evaluate against the identified user AND their groups (the org set via
 * {@link setUserGroup}), so org-targeted canary rollouts work out of the box.
 */
export function useFlag(flagKey: string): boolean {
  const enabled = useFeatureFlagEnabled(flagKey);
  // Local preview overrides are stripped from production builds.
  const devFlags = import.meta.env.DEV
    ? (import.meta.env.VITE_DEV_FEATURE_FLAGS ?? '').split(',')
    : [];
  return devFlags.includes(flagKey) || (enabled ?? false);
}

/**
 * Reactively read a multivariate flag's payload (or `undefined`). Use for flags
 * that carry config (e.g. a canary variant's parameters), not just on/off.
 */
export function useFlagPayload<T = unknown>(flagKey: string): T | undefined {
  return useFeatureFlagPayload(flagKey) as T | undefined;
}

/**
 * Imperative (non-hook) flag check for use outside React render — event
 * handlers, loaders, plain functions. Returns `false` until flags have loaded.
 * Prefer {@link useFlag} inside components so the UI reacts to flag changes.
 */
export function isFlagEnabled(flagKey: string): boolean {
  if (!getPosthogKey()) return false;
  if (!posthog.__loaded) return false;
  return posthog.isFeatureEnabled(flagKey) ?? false;
}
